import { Component, inject, signal, OnInit, DestroyRef, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PromptBotService } from '../../../../core/services/prompt-bot.service';
import { Feature, TableActionEvent } from '../../../../core/models/prompt-bot.model';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { InputComponent } from '../../../../shared/components/ui/input/input.component';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { PaginatedTableComponent, TableHeader } from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { PromptDetailModalComponent, PromptDetailData } from '../../../../shared/components/prompt-detail-modal/prompt-detail-modal.component';
import { IconComponent, IconName } from '../../../../shared/icons/icon.component';
import { AuditService } from '../../../../core/services/audit.service';
import { AuditAction, AuditEntityType } from '../../../../core/models/audit-log.model';

@Component({
  selector: 'app-prompt-features',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, InputComponent, AlertComponent, PaginatedTableComponent, MatDialogModule, IconComponent],
  templateUrl: './features.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./features.component.scss']
})
export class PromptFeaturesComponent implements OnInit {
  private botService = inject(PromptBotService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private auditService = inject(AuditService);

  @Output() actionChange = new EventEmitter<void>();

  features = signal<Feature[]>([]);
  isEditMode = signal(false);
  successMessage = signal('');

  currentFeature: Feature = { name: '', keyname: '', is_active: true };
  private previousFeatureState: Feature | null = null;


  headers: TableHeader[] = [
    { label: 'Feature Identity', key: 'name', align: 'text-left' },
    { label: 'Key', key: 'keyname', align: 'text-left' },
    { label: 'Description', key: 'display_description', align: 'text-left' },
    { label: 'Status', key: 'is_active', align: 'text-center', sortable: false },
  ];

  actionOptions = [
    { label: 'View Details', value: 'view', iconName: 'eye' as IconName, isShow: true },
    { label: 'Modify', value: 'edit', iconName: 'squarePen' as IconName, isShow: true },
    { label: 'Archive', value: 'delete', iconName: 'trash' as IconName, isShow: true },
  ];

  ngOnInit() {
    this.loadFeatures();
  }

  loadFeatures(silent: boolean = false) {
    this.botService.getFeatures({ showLoader: !silent })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: Feature[]) => {
          const transformed = data.map(f => ({
            ...f,
            display_description: f.description && f.description.length > 100
              ? f.description.substring(0, 100) + '...'
              : f.description || '--'
          }));
          this.features.set(transformed);
        }
      });
  }

  saveFeature() {
    const { name, keyname, description, is_active } = this.currentFeature;
    const payload: Feature = { name, keyname, description, is_active };

    if (this.isEditMode() && this.currentFeature.id) {
      this.botService.updateFeature(this.currentFeature.id, payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            const oldValue: any = { id: this.currentFeature.id };
            const newValue: any = { id: this.currentFeature.id };
            const fields: (keyof Feature)[] = ['name', 'keyname', 'description', 'is_active'];

            fields.forEach(field => {
              if (this.previousFeatureState?.[field] !== payload[field as keyof Feature]) {
                oldValue[field] = this.previousFeatureState?.[field];
                newValue[field] = payload[field as keyof Feature];
              }
            });

            this.auditService.createAuditLog({
              entityType: AuditEntityType.PROMPT_FEATURE,
              action: AuditAction.UPDATE,
              actionTitle: `Prompt Feature Updated`,
              oldValue,
              newValue,
              note: `Updated prompt feature name ${payload.name} with key ${payload.keyname}`
            }).subscribe({
              next: () => {
                this.resetForm();
                this.loadFeatures(true);
                this.actionChange.emit();
              },
              error: () => {
                this.resetForm();
                this.loadFeatures(true);
                this.actionChange.emit();
              }
            });
          }
        });
    } else {
      this.botService.createFeature(payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.auditService.createAuditLog({
              entityType: AuditEntityType.PROMPT_FEATURE,
              action: AuditAction.CREATE,
              actionTitle: `Prompt Feature Created`,
              oldValue: null,
              newValue: { ...payload, id: res.id },
              note: `Created prompt feature name '${payload.name}' with key '${payload.keyname}'`
            }).subscribe({
              next: () => {
                this.resetForm();
                this.loadFeatures(true);
                this.actionChange.emit();
              },
              error: () => {
                this.resetForm();
                this.loadFeatures(true);
                this.actionChange.emit();
              }
            });
          }
        });
    }
  }

  handleAction(event: TableActionEvent<Feature>) {
    if (event.action === 'view') {
      this.openViewModal(event.row);
    } else if (event.action === 'edit') {
      this.previousFeatureState = { ...event.row };
      this.currentFeature = { ...event.row };
      this.isEditMode.set(true);
    } else if (event.action === 'delete') {

      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        width: '400px',
        data: {
          title: 'Confirm Deletion',
          description: `Are you sure you want to delete the feature '${event.row.name}'?`
        },
        panelClass: 'custom-dialog-container'
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.botService.deleteFeature(event.row.id!)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.auditService.createAuditLog({
                  entityType: AuditEntityType.PROMPT_FEATURE,
                  action: AuditAction.DELETE,
                  actionTitle: `Prompt Feature Deleted`,
                  oldValue: event.row,
                  newValue: null,
                  note: `Deleted prompt feature name '${event.row.name}' with key '${event.row.keyname}'`
                }).subscribe({
                  next: () => {
                    this.loadFeatures(true);
                    this.actionChange.emit();
                  },
                  error: () => {
                    this.loadFeatures(true);
                    this.actionChange.emit();
                  }
                });
              }
            });
        }
      });
    }
  }

  openViewModal(feature: Feature) {
    const modalData: PromptDetailData = {
      title: 'Feature Capability Details',
      fields: [
        { label: 'Feature Name', value: feature.name },
        { label: 'System Key', value: feature.keyname },
        { label: 'Status', value: feature.is_active ? 'Active' : 'Archived' }
      ],
      highlightLabel: 'System Description',
      highlightValue: feature.description || 'No detailed description provided.'
    };

    this.dialog.open(PromptDetailModalComponent, {
      data: modalData,
      width: '600px',
      panelClass: 'custom-modal-panel'
    });
  }

  resetForm() {
    this.currentFeature = { name: '', keyname: '', is_active: true };
    this.previousFeatureState = null;
    this.isEditMode.set(false);
  }

}
